/**
 * EVERYTHING ELSE — and why it is behind one door instead of six tabs.
 *
 * Six nav items are the six places you go in a day. That rule only works
 * if the other things are still reachable, and until now they were not:
 * the nav was short AND the capability was missing, which is not the deal.
 * Anthony, fairly: "I still can't find my way to anything."
 *
 * So this is the door. Grouped, named in the shop's words, one click from
 * anywhere. What is NOT here is anything that belongs to a record —
 * a customer's vehicles live on the customer, a job's parts on the job.
 * This is only for the things that are genuinely their own place.
 */

import { esc, mount } from "../ui/dom.ts";
import { icon } from "../ui/icons.ts";
import { shopWords } from "../ui/words.ts";
import { cap } from "../domain/trade.ts";
import { gapsOn, NOT_BUILT_PILL, NOT_BUILT_GROUP } from "../domain/gaps.ts";
import { currentSide } from "../ui/side.ts";
import { canSeeAccounting, canSeeBooks } from "../domain/books.ts";

type Item = { href: string; label: string; blurb: string; ready: boolean; icon: string };
type Group = { title: string; items: Item[] };

/**
 * Two labels are not the same word in every shop. Fixing them up here
 * rather than inside the table keeps the table readable and puts each
 * exception next to the one entry it is about. The href never changes --
 * a URL is not copy.
 */
const itemLabel = (i: Item): string => {
  const w = shopWords();
  if (i.href === "#/vehicles") return cap(w.things);
  if (i.href === "#/parts") return cap(w.partsLabel);
  return i.label;
};

/**
 * ─── BUILT AT RENDER, AND FILTERED BY TRADE ────────────────────────────
 *
 * Anthony, 2026-09-18: "hide the parts lookup page for non-auto trades."
 *
 * TWO THINGS WERE WRONG HERE AND ONE OF THEM I PUT IN YESTERDAY.
 *
 * This was a module-level const, so `cap(shopWords().partsLabel)` in the
 * lookup row below was evaluated when the file was first imported --
 * before anybody has signed in and therefore before primeWords() has run.
 * It froze on the auto fallback and would have read "Parts lookup" for
 * every shop no matter what their vocabulary said. Same trap as the
 * runbook. A const cannot ask a question whose answer arrives later.
 *
 * And the row should not be there at all for most trades. It is a live
 * AUTO PARTS CATALOG, searched by vehicle -- the PartsTech key in the
 * environment. A lock shop and a private investigator are being shown a
 * coming-soon promise for a thing that will never apply to them, which is
 * worse than a gap: "not built" reads as "not built YET".
 *
 * `items` is a function per group so a row can be dropped rather than
 * rendered greyed out.
 */
const groups = (profileId: string | null, accountId: string | null = null): Group[] => {
  const w = shopWords();
  return [
  {
    title: "The work",
    items: [
      { href: "#/queue", label: "Needs a person", blurb: "Everybody waiting on a human, and who has them", ready: true, icon: "comms" },
      { href: "#/photos", label: "Photos", blurb: "Every shot on every job", ready: true, icon: "inspections" },
      /* A case file has no vehicles and no parts suppliers (trade.ts caseFile,
         Neelum 2026-10-14). The rows are dropped, not greyed. */
      ...(w.caseFile ? [] : [
        { href: "#/vehicles", label: "Vehicles", blurb: "Every one on the books", ready: true, icon: "vehicles" },
        { href: "#/parts", label: "Parts", blurb: "Suppliers, and what the customer pays", ready: true, icon: "parts" }]),
      { href: "#/comms", label: "Comms", blurb: "Every call, text and email — reading only", ready: true, icon: "comms" },
      { href: "#/contacts", label: "Contacts", blurb: "Everyone you can reach, one tap to call", ready: true, icon: "customers" },
      { href: "#/me", label: "My page", blurb: "Your experience and your certifications", ready: true, icon: "team" },
      /* Manuals is recalls, wiring diagrams, factory portals and a VIN
         decoder. Not a wording problem and not fixable by one: every
         source behind it is a vehicle manufacturer. Same gate as the
         catalog. Flagged rather than quietly dropped -- if a trade ever
         wants its own manuals shelf, this is where it goes. */
      ...(w.hasPartsCatalog
        ? [{ href: "/manuals", label: "Manuals", blurb: "Recalls, wiring diagrams, factory portals and the repair AI", ready: true, icon: "manuals" }]
        : []),
    ],
  },
  {
    title: "Money",
    items: [
      /* The owners' books, built by Kubera. Anthony, 2026-10-07: "me and
         Alaina only can see this." The server checks the same list. */
      ...(canSeeBooks(profileId)
        ? [{ href: "#/books", label: "Books", blurb: "What we made, what we spent, who owes us, and ask Kubera", ready: true, icon: "desk" }]
        : []),
      /* This shop's live money. Anthony, 2026-10-08: "just for us two."
         Only inside Supercanic, so it never shows in another tenant. */
      ...(canSeeAccounting(profileId, accountId)
        ? [{ href: "#/accounting", label: "Accounting", blurb: "What came in, what is owed to us, and what is coming", ready: true, icon: "invoices" }]
        : []),
      { href: "#/invoices", label: "Invoices", blurb: "What is owed, and what has been paid", ready: true, icon: "invoices" },
      { href: "#/authorizations", label: "Authorizations", blurb: "Every yes on file, signed or spoken", ready: true, icon: "authorizations" },
      ...(w.caseFile ? [] : [
        { href: "#/partconditions", label: "Parts with no condition", blurb: "State each one new, used, rebuilt or recon", ready: true, icon: "invoices" }]),
      { href: "#/pay", label: "Pay", blurb: "Hours, the pay run, payouts and your own", ready: true, icon: "payroll" },
      { href: "#/clock", label: "Time clock", blurb: "Clock in and out, and see today's punches", ready: true, icon: "payroll" },
      { href: "#/timesheet", label: "Timesheets", blurb: "Every shift by the week, and fixing one", ready: true, icon: "payroll" },
      { href: "#/terminal", label: "Counter", blurb: "Take a card at the desk", ready: true, icon: "invoices" },
    ],
  },
  {
    title: "Admin",
    items: [
      { href: "#/reports", label: "Reports", blurb: "Revenue, leads, conversion", ready: true, icon: "desk" },
      { href: "#/calls", label: "Calls", blurb: "Search every call by what was said, and see what it turned into", ready: true, icon: "comms" },
      { href: "#/automations", label: "Automations", blurb: "Everything that runs without you, and its switch", ready: true, icon: "settings" },
      { href: "#/desk", label: "Text Desk", blurb: "What the crew texted, what it did, and what is waiting on you", ready: true, icon: "comms" },
      /* Agreements for signature. On a case file they go out from the lead
         and the case instead (Anthony, 2026-10-16). */
      ...(w.caseFile ? [] : [
        { href: "#/agreements", label: "Agreements", blurb: "Send one to sign, and every one sent", ready: true, icon: "authorizations" }]),
      { href: "#/team", label: "Team", blurb: "Everybody, and what they can do", ready: true, icon: "team" },
      { href: "#/certs", label: "Certifications", blurb: "What the shop can honestly claim", ready: true, icon: "team" },
      { href: "#/settings", label: "Shop settings", blurb: "Details and the labor rate", ready: true, icon: "settings" },
      { href: "#/duplicates", label: "Duplicates", blurb: "The same person or job twice — and merge them", ready: true, icon: "customers" },
      { href: "#/shops", label: "All shops", blurb: "Every account on the system", ready: true, icon: "shop" },
      { href: "#/billing", label: "Plan and billing", blurb: "What this shop is on", ready: true, icon: "invoices" },
    ],
  },
  {
    title: "Learning it",
    items: [
      /* The same row on both sides of an RV yard's desk. On Rentals it opens
         the nine lanes a trip runs through, on Repair the eighteen steps. */
      currentSide() === "rentals"
        ? { href: "#/workflow", label: "How a rental runs", blurb: "The nine lanes, from asking to the deposit back", ready: true, icon: "manuals" }
        : { href: "#/workflow", label: "The job, end to end", blurb: "The whole process, with a map", ready: true, icon: "manuals" },
      { href: "#/walkthroughs", label: "How-to manual", blurb: "How to use Shop Desk, shelved by when you need it", ready: true, icon: "manuals" },
      { href: "#/forum", label: "Forum", blurb: "Every shop on Shop Desk, asking and answering each other", ready: true, icon: "comms" },
    ],
  },
  {
    /**
     * ─── THE ROWS COME FROM THE REGISTRY, NOT FROM THIS FILE ───────────
     *
     * These two were typed here, which is how the same list on the
     * process page came to tell Anthony on 2026-09-17 that his inspection
     * sheet was not built, five months after it shipped. A row saying a
     * thing does not exist has nothing that goes with the thing when it
     * lands, so it never stops rendering on its own.
     *
     * src/domain/gaps.ts carries each one with the pattern that would
     * mean it is finished, and tools/gap-check.mjs fails `npm run check`
     * the moment one comes true. Adding the row is no longer the job;
     * deleting the registry entry is, and the build says so by name.
     *
     * THE TRADE GATE STAYS HERE, because it is about who is reading and
     * not about what is built. The catalog is a VEHICLE parts catalog and
     * the lookups are its history: a door company buys doors and an
     * investigator buys database searches. Showing either of them a
     * coming-soon promise is worse than a gap, because "not built" reads
     * as "not built YET".
     */
    title: NOT_BUILT_GROUP,
    items: gapsOn("more", w.hasPartsCatalog).map((g) => ({
      href: "#/more",
      label: g.title.replace(/\{Parts\}/g, () => cap(w.partsLabel)),
      blurb: g.body,
      ready: false,
      icon: g.icon ?? "more",
    })),
  },
  ];
};

export function morePage(profileId: string | null = null, accountId: string | null = null): void {
  mount(`<div class="page">
    <header class="page-head"><h1>Everything else</h1>
      <p>The six on the bar are the places you go daily. This is the rest.</p></header>
    ${/* A group whose every row was filtered out renders as a heading over
          nothing, which reads as a screen that failed to load rather than
          as a section that does not apply. "Not built yet" empties
          completely for a trade with no vehicle catalog. */
      groups(profileId, accountId).filter((g) => g.items.length).map((g) => `
      <div class="card">
        <div class="card-head">${esc(g.title)}</div>
        ${g.items.map((i) => i.ready ? `
          <a class="row" href="${i.href}"${i.href.startsWith("/") ? ' target="_blank" rel="noopener"' : ""}>
            <span class="rowicon">${icon(i.icon)}</span>
            <div class="row-main">
              <div class="row-title"><span class="num">${esc(itemLabel(i))}</span></div>
              <div class="row-sub">${esc(i.blurb)}</div>
            </div>
            <div class="row-side"><div class="when">&rarr;</div></div>
          </a>` : `
          <div class="row soon">
            <span class="rowicon">${icon(i.icon)}</span>
            <div class="row-main">
              <div class="row-title"><span class="num">${esc(itemLabel(i))}</span>
                <span class="pill">${esc(NOT_BUILT_PILL)}</span></div>
              <div class="row-sub">${esc(i.blurb)}</div>
            </div>
          </div>`).join("")}
      </div>`).join("")}
    <p class="quiet" style="text-align:center">
      Anything greyed out is on the cutover list, not forgotten.</p>
  </div>`);
}
